import { Component, OnInit, Inject } from '@angular/core';
import { ConfirmDialogComponent, Logger } from 'src/app/@shared';
import { FormControl, FormGroup } from '@angular/forms';
import { Store, VersionStoreService } from '../..';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DEFAULT_PAGING, DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectionModel } from '@angular/cdk/collections';
import { StoreDomain } from '../../models/store-domain.model';
const log = new Logger('VersionStoreDialogComponent');

@Component({
  selector: 'app-version-store-dialog',
  templateUrl: './version-store-dialog.component.html',
  styleUrls: ['./version-store-dialog.component.scss']
})

export class VersionStoreDialogComponent<T extends Store> implements OnInit {

  viewModel$ = combineLatest([
    this.versionStoreService.dialogstore$,
    this.versionStoreService.isLoading$,
    this.versionStoreService.dialogtotalRecords$,
    this.versionStoreService.storeDialogPage$
  ]).pipe(
    map(([stores, isLoading, totalRecords, page]) => {
      return { stores, isLoading, totalRecords, page }
    }),
  );

  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'StoreName', 'ClientKey', 'Address1', 'Address2', 'City', 'State', 'PostalCode', 'Version',];
  versionId: string = '';
  selection = new SelectionModel<StoreDomain<Store>>(true, []);

  constructor(
    private versionStoreService: VersionStoreService<T>,
    private mediaObserver: MediaObserver,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<VersionStoreDialogComponent<T>>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private matSnackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    log.debug('init');
    this.clearSearch();
    this.versionId = this.data.versionId;
    this.versionStoreService.versionId = this.versionId;
    this.versionStoreService.reload();
    this.versionStoreService.storeDialogresetpage();
    this.dialogRef.updatePosition({
      top: `30px`
    });

    const getAlias = (MediaChange: MediaChange[]) => MediaChange[0].mqAlias;
    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'StoreName'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'StoreName', 'ClientKey', 'City', 'Version'];
        }
        else {
          this.displayedColumns = ['select', 'StoreName', 'ClientKey', 'Address1', 'Address2', 'City', 'State', 'PostalCode', 'Version'];
        }
      });
  }

  onSearch(event: any) {
    this.selection.clear();
    this.versionStoreService.dialogSearch(event.target.value);
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.selection.clear();
    this.versionStoreService.dialogSearch('');
  }
  
  filtersForm = new FormGroup({
    search: new FormControl(),
  });

  onSort(sortState: Sort): void {
    this.versionStoreService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.versionStoreService.storeDialogPage(pageEvent);
  }

  associateStore() {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Add',
        message: `Do you want to add to version?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(
      confirmResult => {
        if (confirmResult) {
          this.versionStoreService.associateVersionToStore(this.versionId, this.selection.selected.map(s => s.Detail)).subscribe({
            next: () => {
              this.matSnackBar.open(`Stores added`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.selection.clear();
              this.closeDialog(true);
              this.versionStoreService.resetpage();
              this.versionStoreService.storeDialogresetpage();
              this.versionStoreService.reload();
            },
            error: (error) => {
              if (error.status === 500) {
              log.error('Error adding store to version', error);
              }
              if (error.status === 400) {
                if(error && error.error){
                  this.matSnackBar.open(error.error.Message, 'OK', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
                }                
              }
              this.selection.clear();
            }
          });
        }
      });

  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected(stores: any[]) {
    const selectableRows = stores.filter(store => !store.IsAssociatedToEvent);
    return selectableRows.length > 0 && selectableRows.every(row => this.selection.isSelected(row));
  }

  isSomeSelected(stores: any[]) {
    const selectableRows = stores.filter(store => !store.IsAssociatedToEvent);
    if (selectableRows.length === 0) return false;
    const selectedInViewCount = selectableRows.filter(row => this.selection.isSelected(row)).length;
    return selectedInViewCount > 0 && selectedInViewCount < selectableRows.length;
  }

  hasSelectableRows(stores: any[]): boolean {
    return stores?.some(store => !store.IsAssociatedToEvent) ?? false;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(stores: any[]) {
    const selectableRows = stores.filter(store => !store.IsAssociatedToEvent);
    if (this.isAllSelected(stores)) {
      this.selection.clear();
    } else {
      this.selection.clear();
      selectableRows.forEach(row => this.selection.select(row));
    }
  }

  closeDialog(boolean = false): void {
    this.dialogRef.close(boolean);
  }

}

interface DialogData {
  versionId: string;
}
